import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Layout from '../components/Layout'
import { getMyOrder } from '../lib/myOrders'
import { listOrderItems, getService } from '../lib/catalog'
import { computeOrderTotal, fmtMoney, discountLabel } from '../lib/pricing'
import { shortDate } from '../lib/dates'
import { getDeliveryProofUrl } from '../lib/deliveryPhoto'
import { PAYMENT_LINK, clientStepIndex, clientStepLabel, paymentBadge, CLIENT_STEPS } from '../lib/tracking'
import { SERVICE_LABEL } from '../lib/orders'

const SUPPORT_PHONE = '+19549009969'
const SUPPORT_WHATSAPP = '19549009969'

// Client-facing order detail — spec §5.
//   - Active (anything up to out_for_delivery): Status + Details + payment
//     line, nothing else.
//   - Delivered: photo, detailed receipt (base + overage + add-ons +
//     handling + total paid + weighed lbs), then Details.
// The receipt is always rebuilt with pricing.js — never hand-summed.
export default function OrderDetail() {
  const { orderNumber } = useParams()
  const navigate = useNavigate()

  const [order, setOrder] = useState(null)
  const [service, setService] = useState(null)
  const [items, setItems] = useState([])
  const [photoUrl, setPhotoUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const row = await getMyOrder(orderNumber)
        if (!alive) return
        if (!row) { setError('Order not found.'); setLoading(false); return }
        setOrder(row)
        const [svc, lines] = await Promise.all([
          row.service_id ? getService(row.service_id) : Promise.resolve(null),
          listOrderItems(row.id),
        ])
        if (!alive) return
        setService(svc)
        setItems(lines)
        if (row.delivery_photo_path) {
          getDeliveryProofUrl(row.delivery_photo_path, 60 * 30).then((u) => alive && setPhotoUrl(u))
        }
      } catch (err) {
        if (alive) setError(err?.message || 'Could not load this order.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [orderNumber])

  if (loading) {
    return <Layout><p className="p-6 text-center text-[13px] text-ink-2">Loading…</p></Layout>
  }
  if (error || !order) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-[13px] text-ink-2">{error || 'Not found.'}</p>
          <Link to="/my-orders" className="mt-3 inline-block text-[13px] font-bold text-navy underline">
            ← My orders
          </Link>
        </div>
      </Layout>
    )
  }

  const delivered = order.status === 'delivered'
  const cancelled = order.status === 'cancelled'
  const badge = paymentBadge(order.status, order.is_paid)
  const stepIdx = clientStepIndex(order.status)

  // Live-priced snapshot — even on a delivered ticket we recompute so the
  // breakdown always matches the number the customer paid.
  const q = service
    ? computeOrderTotal({
        service,
        weighedLbs: Number(order.weighed_lbs) || 0,
        bags: order.bags,
        items,
        serviceDiscount: order.wf_discount_type
          ? { type: order.wf_discount_type, value: order.wf_discount_value }
          : null,
      })
    : null

  return (
    <Layout>
      <div className="px-5 pt-5">
        <button
          type="button"
          onClick={() => navigate('/my-orders')}
          className="text-[12px] font-bold text-navy underline"
        >
          ← My orders
        </button>
        <div className="mt-3 flex items-center gap-2">
          <h1 className="font-bricolage text-[20px] font-extrabold text-navy">
            #{order.order_number}
          </h1>
          {badge && <Badge badge={badge} />}
        </div>
        <p className="mt-0.5 text-[12px] text-ink-2">
          {SERVICE_LABEL[order.service] || order.service}
        </p>
      </div>

      <div className="mt-4 space-y-3 px-4 pb-4">
        {/* STATUS — always shown */}
        {!cancelled && (
          <section className="rounded-xl border border-line bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Status</div>
            <div className="mt-1 font-bricolage text-[15px] font-extrabold text-navy">
              {clientStepLabel(order.status)}
            </div>
            <StatusFrieze current={stepIdx} />
          </section>
        )}
        {cancelled && (
          <section className="rounded-xl border border-line bg-[#F9FAFB] p-4">
            <div className="text-[13px] font-bold text-navy">This order was cancelled.</div>
          </section>
        )}

        {/* PAYMENT DUE bar — active only */}
        {badge?.kind === 'due' && (
          <PaymentDueBar amountCents={order.final_total_cents} />
        )}

        {/* DELIVERED — photo + receipt */}
        {delivered && (
          <>
            {photoUrl && (
              <section className="overflow-hidden rounded-xl border border-line bg-white">
                <img src={photoUrl} alt="Delivered" className="block h-auto w-full" />
                <p className="px-3 py-2 text-[10px] uppercase tracking-widest text-ink-3">
                  Delivery photo
                </p>
              </section>
            )}
            {q && <ReceiptCard order={order} service={service} q={q} />}
          </>
        )}

        {/* DETAILS — always shown last */}
        <DetailsCard order={order} />

        {/* Bottom actions — WhatsApp + Call */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <a
            href={`https://wa.me/${SUPPORT_WHATSAPP}?text=Hi%2C%20I%20have%20a%20question%20about%20order%20${encodeURIComponent(order.order_number)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl border border-navy bg-white py-3 text-center text-[13px] font-extrabold text-navy"
          >
            💬 WhatsApp
          </a>
          <a
            href={`tel:${SUPPORT_PHONE}`}
            className="rounded-xl bg-navy py-3 text-center text-[13px] font-extrabold text-white"
          >
            📞 Call
          </a>
        </div>
      </div>
    </Layout>
  )
}

function Badge({ badge }) {
  const style = badge.kind === 'paid'
    ? { color: '#15803D', background: '#DCFCE7', border: '1px solid #86EFAC' }
    : { color: '#D4A017', background: '#FEF6DF', border: '1px solid #F0DCA0' }
  return (
    <span
      style={{ ...style, padding: '2px 8px', borderRadius: 999, fontSize: '9px', fontWeight: 800, letterSpacing: '0.02em', textTransform: 'uppercase' }}
    >
      {badge.text}
    </span>
  )
}

function StatusFrieze({ current }) {
  return (
    <ol className="mt-3 flex items-stretch gap-1" aria-label="Order progress">
      {CLIENT_STEPS.map((s, i) => {
        const done = i <= current
        const isCurrent = i === current
        return (
          <li key={s.key} className="flex flex-1 flex-col items-center gap-1">
            <span
              aria-hidden
              className="h-1.5 w-full rounded-full"
              style={{ background: done ? '#0B2038' : '#E5E7EB' }}
            />
            <span
              className={`text-[9px] text-center leading-tight ${isCurrent ? 'font-extrabold text-navy' : 'text-ink-3'}`}
            >
              {s.label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function PaymentDueBar({ amountCents }) {
  return (
    <div
      className="flex items-start justify-between rounded-xl px-3 py-3"
      style={{ background: '#FEF6DF', border: '1px solid #F0DCA0' }}
    >
      <div className="min-w-0 pr-3">
        <div className="text-[12px] font-extrabold text-navy">💳 Waiting for your payment</div>
        <div className="mt-0.5 text-[11px] leading-relaxed text-navy/80">
          Weighed and priced. We&apos;ll reach out to settle it, then it goes back out.
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="font-bricolage text-[14px] font-extrabold text-navy">
          {fmtMoney(amountCents)}
        </span>
        {PAYMENT_LINK && (
          <button
            type="button"
            className="rounded-md bg-navy px-2.5 py-1 text-[10px] font-extrabold text-white"
          >
            Pay now
          </button>
        )}
      </div>
    </div>
  )
}

function ReceiptCard({ order, service, q }) {
  const rows = []
  if (q.baseCents > 0) {
    rows.push({
      label: `${service?.name || 'Service'}${service?.included_lbs ? ` — up to ${service.included_lbs} lbs` : ''}`,
      cents: q.baseCents,
    })
  }
  if (q.overageCents > 0) {
    rows.push({
      label: `Extra lbs · ${q.overageLbs.toFixed(1)} lbs × $${((service?.extra_lb_cents || 0) / 100).toFixed(2)}/lb`,
      cents: q.overageCents,
    })
  }
  if (q.serviceDiscountCents > 0) {
    rows.push({
      label: 'Wash & Fold discount',
      badge: discountLabel(order.wf_discount_type, order.wf_discount_value),
      reason: order.wf_discount_reason,
      cents: -q.serviceDiscountCents,
    })
  }
  for (const l of q.lines) {
    if (Number(l.lineGrossCents) <= 0) continue
    rows.push({
      label: `${l.name_snapshot}${l.quantity > 1 ? ` × ${l.quantity}` : ''}`,
      cents: l.lineTotalCents,
      grossCents: l.lineDiscountCents > 0 ? l.lineGrossCents : null,
      badge: l.lineDiscountCents > 0 ? discountLabel(l.discount_type, l.discount_value) : null,
      reason: l.lineDiscountCents > 0 ? l.discount_reason : null,
    })
  }
  if (q.handlingCents > 0) {
    rows.push({
      label: `Handling & transportation ${Number(service?.handling_fee_pct || 0)}%`,
      cents: q.handlingCents,
    })
  }
  return (
    <section className="rounded-xl border border-line bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Receipt</div>
      {order.weighed_lbs != null && (
        <div className="mt-1 text-[11px] text-ink-2">
          Weighed: <b className="text-navy">{Number(order.weighed_lbs).toFixed(1)} lbs</b>
        </div>
      )}
      <ul className="mt-2 space-y-1 text-[12px]">
        {rows.map((r, i) => (
          <li key={i} className="flex items-start justify-between gap-3">
            <span className="min-w-0 text-ink-2">
              {r.label}
              {r.badge && (
                <span className="ml-1.5 rounded-full bg-red/10 px-1.5 py-0.5 text-[10px] font-extrabold text-red">
                  Discount {r.badge}
                </span>
              )}
              {r.reason && <span className="block text-[10px] text-ink-3">{r.reason}</span>}
            </span>
            <span className="shrink-0 text-right">
              {r.grossCents != null && (
                <span className="mr-1.5 text-[11px] text-ink-3 line-through">{fmtMoney(r.grossCents)}</span>
              )}
              <span className={r.cents < 0 ? 'text-red' : 'text-navy'}>
                {r.cents < 0 ? `−${fmtMoney(-r.cents)}` : fmtMoney(r.cents)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-between border-t border-line pt-2">
        <span className="text-[10px] font-bold uppercase text-ink-2">Total paid</span>
        <span className="font-bricolage text-[16px] font-extrabold text-navy">
          {fmtMoney(q.totalCents)}
        </span>
      </div>
    </section>
  )
}

function DetailsCard({ order }) {
  const returnLabel = order.pickup_group_id
    // Part of a multi-service pickup: everything comes back in one trip.
    ? 'Confirmed when everything is ready'
    : order.return_tbd
    ? 'Confirmed when ready'
    : order.return_date
    ? `${shortDate(order.return_date)} · ${slotLbl(order.return_slot)}`
    : '—'
  const address = [order.address_line1, order.city, order.state, order.zip].filter(Boolean).join(', ')
  return (
    <section className="rounded-xl border border-line bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Details</div>
      <Row label="Bags" value={order.bags} />
      <Row label="Pickup" value={order.pickup_date ? `${shortDate(order.pickup_date)} · ${slotLbl(order.pickup_slot)}` : '—'} />
      <Row label="Return" value={returnLabel} />
      {order.hotel_name && (
        <Row
          label="Hotel"
          value={`${order.hotel_name}${order.room_number ? ` · Room ${order.room_number}` : ''}`}
        />
      )}
      {address && <Row label="Address" value={address} />}
      {order.notes && (
        <div className="mt-2 border-t border-line pt-2 text-[12px]">
          <div className="text-[10px] font-bold uppercase text-ink-2">Notes</div>
          <p className="mt-1 text-navy">{order.notes}</p>
        </div>
      )}
    </section>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-[12px]">
      <span className="text-ink-2">{label}</span>
      <span className="text-right text-navy">{value}</span>
    </div>
  )
}

function slotLbl(id) {
  if (id === 'morning') return '9–11 AM'
  if (id === 'evening') return '6–9 PM'
  return ''
}
